import React, { useState, useCallback, useEffect } from 'react';
import { FileUpload } from '@/components/ui/file-upload';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { apiService } from '@/apiService';
import { PlaybackStatus } from '@/types';
import {
  Play,
  Pause,
  Square,
  RotateCcw,
  Database,
  Zap,
  Settings,
  Activity,
  Calendar
} from 'lucide-react';

interface ModernPlaybackProps {
  className?: string;
}

export const ModernPlayback: React.FC<ModernPlaybackProps> = ({ className }) => {
  const [symbol, setSymbol] = useState('AAPL');
  const [speed, setSpeed] = useState(1.0);
  const [status, setStatus] = useState<PlaybackStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-01-01`;
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });


  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleAction = async (action: () => Promise<any>, description: string) => {
    try {
      setError(null);
      setLoading(true);
      console.log(`Starting ${description}...`);
      const result = await action();
      console.log(`${description} successful:`, result);
      await fetchStatus();
    } catch (err) {
      console.error(`${description} failed:`, err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred.');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatus = useCallback(async () => {
    try {
      const statusResult = await apiService.getPlaybackStatus();
      setStatus(statusResult);
    } catch (err) {
      console.debug('Status fetch failed:', err);
    }
  }, []);

  const formatProgress = (current: number, total: number): string => {
    if (total === 0) return '0%';
    return `${Math.round((current / total) * 100)}%`;
  };

  const formatTime = (timeStr: string | undefined): string => {
    if (!timeStr) return 'N/A';
    try {
      return new Date(timeStr).toLocaleTimeString();
    } catch {
      return timeStr;
    }
  };

  return (
    <div className={className}>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="h-4 w-4 text-primary" />
            <span>Playback Controls</span>
            {status?.isPlaying && (
              <div className="flex items-center gap-1.5 ml-2">
                <div className="w-2 h-2 bg-emerald-500 rounded-full pulse-live" />
                <span className="text-xs text-emerald-500 font-normal">Playing</span>
              </div>
            )}
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-5">
          {/* Error Display */}
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
              {error}
            </div>
          )}

          {/* Symbol Input */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Symbol</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                placeholder="e.g., AAPL"
                className="flex-1 px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <Button variant="outline" size="sm" onClick={() => setSymbol('AAPL')}>
                Reset
              </Button>
            </div>
          </div>

          {/* Speed Control */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Speed: <span className="text-foreground">{speed}x</span>
              <span className="text-muted-foreground/70 font-normal ml-2">(1 min bar = {(60 / speed).toFixed(1)}s)</span>
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="1"
                max="10"
                step="1"
                value={speed}
                onChange={(e) => setSpeed(parseFloat(e.target.value))}
                className="flex-1 accent-primary"
              />
              <div className="flex gap-1">
                {[1, 2, 5, 10].map(s => (
                  <Button key={s} variant={speed === s ? 'default' : 'outline'} size="sm" className="h-7 px-2 text-xs" onClick={() => setSpeed(s)}>
                    {s}x
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Date Range */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              Date Range
            </label>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div className="flex gap-1.5">
              {[
                { label: 'Last Week', fn: () => {
                  const today = new Date();
                  const ago = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
                  setStartDate(ago.toISOString().split('T')[0]);
                  setEndDate(today.toISOString().split('T')[0]);
                }},
                { label: 'Last Month', fn: () => {
                  const today = new Date();
                  const ago = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
                  setStartDate(ago.toISOString().split('T')[0]);
                  setEndDate(today.toISOString().split('T')[0]);
                }},
                { label: '2024', fn: () => {
                  const y = new Date();
                  setStartDate(`${y.getFullYear()}-01-01`);
                  setEndDate(y.toISOString().split('T')[0]);
                }},
              ].map(preset => (
                <Button key={preset.label} variant="outline" size="sm" className="h-7 text-xs" onClick={preset.fn}>
                  {preset.label}
                </Button>
              ))}
            </div>
          </div>


          {/* Data Source Controls */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Data Sources</label>
            <div className="grid grid-cols-1 gap-2">
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleAction(
                    () => apiService.startSampleReplay(symbol, startDate, endDate, true, speed),
                    'Sample replay'
                  )}
                  disabled={loading}
                  className="h-8 text-xs"
                >
                  <Zap className="h-3.5 w-3.5 mr-1.5" />
                  Sample Data
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleAction(
                    () => apiService.loadFromDatabase([symbol], startDate, endDate),
                    'Database load'
                  )}
                  disabled={loading}
                  className="h-8 text-xs"
                >
                  <Database className="h-3.5 w-3.5 mr-1.5" />
                  From Database
                </Button>
              </div>
              <FileUpload
                onFileUpload={(file) => {
                  console.log('File uploaded:', file.name, file.size, 'bytes');
                  handleAction(
                    () => apiService.loadFromCsv(file),
                    `CSV upload (${file.name})`
                  );
                }}
              />
            </div>
          </div>

          {/* Playback Controls */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Controls</label>
            <div className="flex gap-1.5">
              <Button
                size="sm"
                onClick={() => handleAction(
                  () => apiService.startDatabasePlayback(symbol, speed),
                  'Database playback start'
                )}
                disabled={loading || status?.isPlaying}
                className="h-8 text-xs flex-1"
              >
                <Play className="h-3.5 w-3.5 mr-1" />
                Start
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAction(apiService.pausePlayback, 'Playback pause')}
                disabled={loading || !status?.isPlaying}
                className="h-8 text-xs"
              >
                <Pause className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAction(apiService.stopPlayback, 'Playback stop')}
                disabled={loading}
                className="h-8 text-xs"
              >
                <Square className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAction(() => apiService.resetDatabasePlayback(), 'Playback reset')}
                disabled={loading}
                className="h-8 text-xs"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAction(
                  () => apiService.configureDatabase(speed),
                  'Configuration update'
                )}
                disabled={loading}
                className="h-8 text-xs"
              >
                <Settings className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Status Display */}
          {status && (
            <div className="space-y-2 p-3 rounded-md bg-muted/30 border border-border/50">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</label>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">State</span>
                  <span className={`font-medium ${status.isPlaying ? 'text-emerald-500' : 'text-muted-foreground'}`}>
                    {status.isPlaying ? 'Playing' : 'Stopped'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Speed</span>
                  <span className="font-mono tabular-nums">{status.playbackSpeed}x</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Progress</span>
                  <span className="font-mono tabular-nums">{formatProgress(status.processedTicks, status.totalTicks)}</span>
                </div>
                {status.currentPlaybackTime && (
                  <div className="col-span-2 flex justify-between">
                    <span className="text-muted-foreground">Current Time</span>
                    <span className="font-mono tabular-nums">{formatTime(status.currentPlaybackTime)}</span>
                  </div>
                )}
                {status.dataSource && (
                  <div className="col-span-2 flex justify-between">
                    <span className="text-muted-foreground">Source</span>
                    <span className="capitalize">{status.dataSource}</span>
                  </div>
                )}
              </div>

              {/* Progress Bar */}
              {status.totalTicks > 0 && (
                <div className="space-y-1 pt-1">
                  <div className="flex justify-between text-[10px] text-muted-foreground">
                    <span className="tabular-nums">{status.processedTicks.toLocaleString()} / {status.totalTicks.toLocaleString()}</span>
                    <span className="tabular-nums">{status.progressPercent.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-secondary rounded-full h-1.5 overflow-hidden">
                    <div
                      className="bg-primary rounded-full h-1.5 transition-all duration-300"
                      style={{ width: `${Math.min(100, status.progressPercent)}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ModernPlayback;
